import apiClient from './client';
import { MOCK_PARTS, PART_CATEGORIES } from '@/app/lib/mock-db';

export interface Category {
    id: number;
    name: string;
    name_ar: string;
    slug: string;
    icon?: string;
    image?: string;
}

export interface ProductVariant {
    id: number;
    sku: string;
    barcode?: string;
    name_suffix: string;
    price: string;
    effective_price: string;
    is_active?: boolean;
}

export interface ProductImage {
    id: number;
    image: string;
    alt_text: string;
    is_primary: boolean;
}

export interface ProductTemplate {
    id: number;
    name: string;
    name_ar: string;
    description: string;
    description_ar: string;
    base_price: string;
    compare_price?: string | null;
    cost?: string;
    customer_taxes?: string[];
    can_be_sold?: boolean;
    can_be_purchased?: boolean;
    can_be_expensed?: boolean;
    product_type?: 'stockable' | 'consumable' | 'service';
    invoicing_policy?: 'order' | 'delivery';
    internal_reference?: string;
    barcode?: string;
    sku_id?: string;
    location_label?: string;
    tags?: string[];
    internal_notes?: string;
    website_description?: string;
    website_description_ar?: string;
    customer_description?: string;
    customer_description_ar?: string;
    approval_status?: string;
    rejection_reason?: string;
    category: number;
    category_name: string;
    category_name_ar?: string;
    variants: ProductVariant[];
    images: ProductImage[];
}

export interface ProductFieldConfig {
    key: string;
    label: string;
    type: string;
    visible: boolean;
    required: boolean;
    editable: boolean;
    show_in_customer: boolean;
    include_in_import: boolean;
}

function isNetworkFailure(error: unknown): boolean {
    return Boolean(
        error &&
        typeof error === 'object' &&
        'isAxiosError' in error &&
        (error as any).isAxiosError &&
        !(error as any).response
    );
}

const FALLBACK_CATEGORIES: Category[] = PART_CATEGORIES.map((nameAr, index) => ({
    id: index + 1,
    name: ['Engine', 'Brakes', 'Suspension', 'Electrical', 'Oils'][index] || `Category ${index + 1}`,
    name_ar: nameAr,
    slug: `local-${index + 1}`,
}));

const FALLBACK_PRODUCTS: ProductTemplate[] = MOCK_PARTS.map((part, index) => {
    const categoryIndex = Math.max(PART_CATEGORIES.indexOf(part.category), 0);
    const category = FALLBACK_CATEGORIES[categoryIndex] || FALLBACK_CATEGORIES[0];
    const price = String(part.price);

    return {
        id: Number(part.id.replace(/\D/g, '')) || index + 1,
        name: part.name_ar,
        name_ar: part.name_ar,
        description: part.brand,
        description_ar: part.brand,
        base_price: price,
        category: category.id,
        category_name: category.name,
        category_name_ar: category.name_ar,
        variants: [
            {
                id: index + 1,
                sku: part.sku || `LOCAL-${index + 1}`,
                name_suffix: part.brand,
                price,
                effective_price: price,
                is_active: true,
            },
        ],
        images: [
            {
                id: index + 1,
                image: part.image_url,
                alt_text: part.name_ar,
                is_primary: true,
            },
        ],
    };
});

export const catalogApi = {
    getCategories: async () => {
        try {
            const { data } = await apiClient.get<any>('/catalog/categories/');
            return data.results ? data.results : data;
        } catch (error) {
            if (isNetworkFailure(error)) return FALLBACK_CATEGORIES;
            throw error;
        }
    },

    getProducts: async (params?: Record<string, string>) => {
        try {
            const { data } = await apiClient.get<{ count: number, results: ProductTemplate[] }>('/catalog/products/', { params });
            return data;
        } catch (error) {
            if (isNetworkFailure(error)) {
                const search = String(params?.search || '').trim().toLowerCase();
                const categoryId = params?.category ? Number(params.category) : null;
                let rows = FALLBACK_PRODUCTS;

                if (categoryId) rows = rows.filter((product) => product.category === categoryId);
                if (search) {
                    rows = rows.filter((product) => (
                        `${product.name} ${product.name_ar} ${product.category_name} ${product.category_name_ar || ''}`
                            .toLowerCase()
                            .includes(search)
                    ));
                }

                return { count: rows.length, results: rows };
            }
            throw error;
        }
    },

    getProduct: async (id: string | number) => {
        const { data } = await apiClient.get<ProductTemplate>(`/catalog/products/${id}/`);
        return data;
    },

    getProductFieldConfig: async () => {
        const { data } = await apiClient.get<{ fields: ProductFieldConfig[] }>('/catalog/product-fields/');
        return data.fields || [];
    }
};
